import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, FileText, HelpCircle, Calendar } from "lucide-react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/hooks/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolvePdf = (url) => {
    if (!url) return "#";
    if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
    return url;
};

const STATUS = {
    obowiazujaca: { label: "Obowiązująca", cls: "bg-emerald-100 text-emerald-800" },
    archiwalna: { label: "Archiwalna", cls: "bg-gray-200 text-gray-700" },
    projekt: { label: "Projekt", cls: "bg-amber-100 text-amber-800" },
};

export default function UchwalaDetailPage() {
    const { id } = useParams();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        api.get(`/resolutions/${id}`)
            .then((r) => setItem(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [id]);

    const meta = item ? (STATUS[item.status] || STATUS.obowiazujaca) : STATUS.obowiazujaca;
    useSeo({
        title: item ? `Uchwała ${item.number} – ${item.title}` : undefined,
        description: item?.description || "",
        type: "article",
    });

    return (
        <div data-testid="uchwala-detail-page" className="min-h-screen bg-[#FDFDFD]">
            <Navbar />
            <header className="bg-[#092E22] text-white pt-32 pb-16">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <Link to="/#uchwaly" data-testid="back-to-uchwaly" className="inline-flex items-center gap-2 text-[#D4A537] text-sm hover:underline mb-6">
                        <ArrowLeft size={14} /> Powrót do uchwał
                    </Link>
                    {loading && <p className="text-white/70">Wczytywanie...</p>}
                    {notFound && <p className="text-white/70">Nie znaleziono uchwały.</p>}
                    {item && (
                        <>
                            <div className="flex items-center gap-3 mb-4 flex-wrap">
                                <span className="inline-flex items-center gap-1 text-xs font-mono text-[#D4A537] font-semibold">
                                    <FileText size={12} /> {item.number}
                                </span>
                                <span className={`inline-flex items-center px-3 py-1 rounded-full ${meta.cls} text-xs font-semibold uppercase tracking-wider`}>
                                    {meta.label}
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-white/70 text-xs">
                                    <Calendar size={12} /> Rok {item.year}
                                </span>
                            </div>
                            <h1 className="font-serif text-4xl md:text-5xl leading-tight">{item.title}</h1>
                        </>
                    )}
                </div>
            </header>

            {item && (
                <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
                    <motion.article
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 md:p-12"
                        data-testid="uchwala-content"
                    >
                        {item.description && (
                            <div className="text-gray-700 leading-relaxed text-base md:text-lg whitespace-pre-line mb-8">
                                {item.description}
                            </div>
                        )}
                        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
                            {item.pdf_url && (
                                <a
                                    href={resolvePdf(item.pdf_url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="uchwala-detail-pdf"
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0D4F3C] text-white text-sm font-medium hover:bg-[#092E22] transition-all hover:-translate-y-0.5"
                                >
                                    <Download size={16} /> Pobierz PDF uchwały
                                </a>
                            )}
                            {item.doc_url && (
                                <a
                                    href={resolvePdf(item.doc_url)}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="uchwala-detail-doc"
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0D4F3C]/10 text-[#0D4F3C] text-sm font-medium hover:bg-[#0D4F3C]/20 transition-all hover:-translate-y-0.5"
                                >
                                    <FileText size={16} /> Pobierz DOC uchwały
                                </a>
                            )}
                            <Link
                                to="/jak-glosowac"
                                data-testid="uchwala-detail-voting"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#D4A537] text-[#0D4F3C] text-sm font-medium hover:bg-[#D4A537]/10 transition-all hover:-translate-y-0.5"
                            >
                                <HelpCircle size={16} /> Jak głosować
                            </Link>
                        </div>
                    </motion.article>
                </main>
            )}

            <Footer />
        </div>
    );
}
